import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateWasteProducerMutation } from '../../../../hooks/mutations/useWasteProducerMutation';
import { useWasteProducersQuery } from '../../../../hooks/queries/useWasteProducersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { WasteProducer } from '../../../../services/apiSLTP/sites/wasteProducers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateWasteProducerModal from '../../../modals/admin/internal/UpdateWasteProducerModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface WasteProducersSettingsRowProps {}

const WasteProducersSettingsRow: React.FC<WasteProducersSettingsRowProps> = ({}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedWasteProducer, setSelectedWasteProducer] = useState<WasteProducer | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { data: wasteProducers, isLoading: isWasteProducersLoading, refetch: refetchWasteProducers } = useWasteProducersQuery({});

    const { mutate: mutateWasteProducer } = useUpdateWasteProducerMutation({
        onSuccess: () => refetchWasteProducers(),
        onError: (error) => console.error('Error updating waste producer:', error.message),
    });

    const columns: ColDef<WasteProducer>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 300,
            colId: 'designation',
        },
        {
            headerName: capitalizeFirstLetter(t('siret')),
            field: 'siret',
            colId: 'siret',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<WasteProducer>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyWasteProducer'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedWasteProducer(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteWasteProducer'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedWasteProducer(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<WasteProducer>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('contactLastName')),
            field: 'contactLastName',
            colId: 'contactLastName',
        },
        {
            headerName: capitalizeFirstLetter(t('contactFirstName')),
            field: 'contactFirstName',
            colId: 'contactFirstName',
        },
        {
            headerName: capitalizeFirstLetter(t('contactEmail')),
            field: 'contactEmail',
            colId: 'contactEmail',
        },
        {
            headerName: capitalizeFirstLetter(t('contactPhone')),
            field: 'contactPhone',
            colId: 'contactPhone',
        },
        createDateTimeColumn<WasteProducer>('createdAt', 'createdAt'),
        createDateTimeColumn<WasteProducer>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columns.push({
            headerName: capitalizeFirstLetter(t('_id')),
            field: '_id',
            minWidth: 250,
            cellClass: 'usage-col-main',
            headerClass: 'usage-col-main',
            colId: '_id',
        });
    }

    const sortedWasteProducers = useMemo(() => {
        return wasteProducers?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [wasteProducers]);

    return (
        <>
            <GenericAgGridTable
                columnDefs={columns}
                rowData={sortedWasteProducers.filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                loading={isWasteProducersLoading}
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedWasteProducer(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addWasteProducer'))}
                    />
                }
                exportCsvFileName="admin-waste-producers"
                storageKey={localStorageKeys.TABLE.ADMIN.INTERNAL.WASTEPRODUCERS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateWasteProducerModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    wasteProducer={selectedWasteProducer}
                    isEditMode={isEditMode}
                    refetch={refetchWasteProducers}
                />
            )}
            {showDeleteModal && selectedWasteProducer && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateWasteProducer({ id: selectedWasteProducer._id, data: { crudStatus: crudStatusEnum.DELETED } });
                        setShowDeleteModal(false);
                    }}
                    title={t('wasteProducer')}
                    itemName={selectedWasteProducer.designation}
                />
            )}
        </>
    );
};

export default WasteProducersSettingsRow;
